import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { extractErr } from "@/lib/api";
import { useLocations } from "@/lib/locations";
import { useAuth } from "@/lib/auth";
import { EmptyState } from "@/components/EmptyState";
import { PlanGate } from "@/components/PlanGate";
import { Plus, MapPin, Pencil, Trash2, Star, StarOff, Building2 } from "lucide-react";

export default function Locations() {
  const { company } = useAuth();
  const ctx = useLocations() || {};
  const { locations = [], create, update, remove, refresh } = ctx;
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const plan = company?.plan || "free_trial";
  const canMulti = plan === "enterprise" || plan === "free_trial";

  useEffect(() => { refresh?.(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onSave = async (form) => {
    try {
      if (editing) await update(editing.id, form);
      else await create(form);
      setOpen(false); setEditing(null);
    } catch (e) { /* toast already in lib */ }
  };

  if (!canMulti && locations.length >= 1) {
    return (
      <AppShell title="Locations" subtitle="Manage warehouses, stores, and production sites">
        <PlanGate feature="Multi-location" planRequired="Enterprise">
          <div className="h-32" />
        </PlanGate>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Locations"
      subtitle={`${locations.length} location${locations.length === 1 ? "" : "s"}`}
      headerActions={(
        <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="locations-create-button">
          <Plus className="w-4 h-4 mr-1.5" /> New location
        </Button>
      )}
    >
      {locations.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No locations yet"
          description="Create locations (warehouses, stores, production sites) and assign inventory to them. Enterprise plans support unlimited locations."
          action={<Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="locations-empty-create"><Plus className="w-4 h-4 mr-1.5" /> Add location</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {locations.map((loc) => (
            <div key={loc.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`location-card-${loc.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-base truncate">{loc.name}</div>
                    {loc.address && <div className="text-xs text-muted-foreground truncate">{loc.address}</div>}
                    {loc.is_default && (
                      <span className="inline-flex items-center gap-1 mt-1.5 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] uppercase tracking-wide">
                        <Star className="w-3 h-3" /> Default
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!loc.is_default && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Set as default" onClick={() => update(loc.id, { is_default: true })} data-testid={`location-set-default-${loc.id}`}>
                      <StarOff className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(loc); setOpen(true); }} data-testid={`location-edit-${loc.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {!loc.is_default && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`location-delete-${loc.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove location?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will deactivate “{loc.name}”. Historical data will remain attached for reporting.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => remove(loc.id)}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
              {loc.notes && <div className="mt-3 text-xs text-muted-foreground line-clamp-2">{loc.notes}</div>}
            </div>
          ))}
        </div>
      )}

      {open && (
        <LocationForm
          open={open}
          initial={editing}
          canMulti={canMulti}
          existingCount={locations.length}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSave={onSave}
        />
      )}
    </AppShell>
  );
}

function LocationForm({ open, onClose, onSave, initial, canMulti, existingCount }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    address: initial?.address || "",
    notes: initial?.notes || "",
    is_default: !!initial?.is_default,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setForm({
      name: initial?.name || "",
      address: initial?.address || "",
      notes: initial?.notes || "",
      is_default: !!initial?.is_default,
    });
  }, [initial]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try { await onSave(form); }
    catch (err) { /* handled */ }
    setSubmitting(false);
  };

  const blockedNew = !initial && !canMulti && existingCount >= 1;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit location" : "New location"}</DialogTitle>
          <DialogDescription>
            Locations represent warehouses, stores, or production sites. Each location has its own inventory and orders.
          </DialogDescription>
        </DialogHeader>
        {blockedNew ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm">
            <div className="font-medium text-amber-600 dark:text-amber-400">Enterprise plan required</div>
            <p className="text-muted-foreground mt-1">Multiple locations are available on the Enterprise plan. Please upgrade to create additional locations.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="location-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => update("address", e.target.value)} data-testid="location-form-address" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div>
                <Label className="text-sm">Default location</Label>
                <div className="text-[11px] text-muted-foreground">New items and orders use this location by default.</div>
              </div>
              <Switch checked={form.is_default} onCheckedChange={(v) => update("is_default", v)} data-testid="location-form-default" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="location-form-submit">{submitting ? "Saving…" : initial ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
